import base64 from 'base-64'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Icon,
  PasswordTextfield,
  SsoLogin,
  Text,
  TextField,
  Title,
  safeLocalStorage,
} from 'ui'
import { z } from 'zod'
import { useNavigationContext } from '~/context/NavigationContext'
import {
  existingUser,
  loginPassChange,
  novaTried,
  returnToCookie,
  userDataForTracking,
} from '~/cookie'
import { showCustomNotification } from '~/helperFunctions/customNotifications'
import { authenticator } from '~/services/auth.server'
import classes from '~/styles/login/login.module.scss'
import {
  clearClientStateForLogin,
  clearLoginSessionLocalStorage,
  clearLogoutDomainAwareCookies,
  purgeBrowserCookies,
} from '~/utils/authCleanup'
import { clientDeleteCookie } from '~/utils/commonUtils'
import {
  ALL_COOKIE_LIST,
  MAXIMUM_RETRY_SSO,
  REDIRECT_URL,
  SESSION_EXPIRED_MESSAGE,
  SSO_LOGIN_LIST,
  UTMCOOKIE_LIST,
  logoutCookieToClearWithDomain,
} from '~/utils/constant'
import { inferSafeParseErrors } from '~/utils/inferSafeParseErrors'
import { manipulateUtm } from '~/utils/manipulateUtm'
import { setObjectCookie } from '~/utils/setObjectCookie'

import { Container, Divider, Flex, Grid, Space } from '@mantine/core'
import { useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { ActionFunction, LoaderFunction, json, redirect } from '@remix-run/node'
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
  useSearchParams,
  useSubmit,
} from '@remix-run/react'

import { sessionStorage } from '../../services/session.server'
import { containsOutsideLatin1 } from '../../utils/containsOutsideLatin1'

export const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(2),
  rememberMe: z.string().optional(),
})
export interface LoginLoaderData<Fields = unknown, Errors = unknown> {
  fields: Fields
  errors?: Errors
  dataType?: 'actionData' | 'loaderData'
  status: boolean
  exisitingUser?: boolean
  currentDomain?: string
  currentSubDomain?: string
  ssoError?: string
}

type SchemaFields = z.infer<typeof schema>
type SchemaFieldsErrors = inferSafeParseErrors<typeof schema>
type ActionData = LoginLoaderData<SchemaFields, SchemaFieldsErrors>

export const action: ActionFunction = async ({ request, context }) => {
  try {
    const user = await authenticator.authenticate('user-pass', request)
    const cookie = request.headers.get('cookie')
    if (user?.status == 'Error') {
      return json({
        status: 'error',
        errors: {
          fieldErrors: {
            email: [user?.message],
          },
          dataType: 'loaderData',
        },
      })
    }
    const returnTo = await returnToCookie.parse(cookie)
    const resolveReturnTo = (value: string | null | undefined) => {
      if (!value) return '/dashboard'
      try {
        const path = /^https?:\/\//i.test(value) ? new URL(value).pathname : value.split('?')[0]
        if (path.replace(/\/+$/, '') === '/logout') return '/dashboard'
      } catch {
        // fall through
      }
      return value
    }

    const session = await sessionStorage.getSession(cookie)
    session.set(authenticator.sessionKey, user)
    const headers = new Headers({ 'Set-Cookie': await sessionStorage.commitSession(session) })
    headers.append(
      'set-cookie',
      await userDataForTracking.serialize({
        id: user?.userdetails?.userId,
        email: user?.userdetails?.email,
      }),
    )
    headers.append('set-cookie', await novaTried.serialize(false, { maxAge: 0 }))
    headers.append('set-cookie', await returnToCookie.serialize(null, { maxAge: 0 }))

    if (user.firstTimeLogin) {
      headers.append('set-cookie', await loginPassChange.serialize({ changePass: true }))

      return redirect('/login/changePassword', { headers })
    } else {
      return redirect(resolveReturnTo(returnTo), { headers })
    }
  } catch (error: any) {
    return json({
      status: 'error',
      errors: {
        fieldErrors: {
          email: [error?.message || 'Login failed. Please check your credentials.'],
        },
      },
    })
  }
}

export const loader: LoaderFunction = async ({ request }) => {
  const changePassCookie = (await loginPassChange.parse(request.headers.get('Cookie'))) || {}
  if (changePassCookie?.changePass) {
    return redirect('/login/changePassword')
  }
  const session = await sessionStorage?.getSession(request?.headers?.get('Cookie'))
  await authenticator.isAuthenticated(request, {
    successRedirect: '/dashboard',
  })
  try {
    const url = new URL(request?.url)
    const ssoError = url?.searchParams?.get('error')
    if (ssoError) {
      return json({
        dataType: 'loaderData',
        ssoError,
      })
    }
    let exisitingUser = false
    const existingUserCookieData = (await existingUser.parse(request.headers.get('Cookie'))) || {}
    if (existingUserCookieData?.userExist) {
      exisitingUser = true
    }

    const session = await sessionStorage.getSession(request.headers.get('cookie'))
    const error = session.get(authenticator.sessionErrorKey)
    if (error?.message) {
      return json(
        {
          errors: {
            fieldErrors: {
              email: [error?.message],
            },
          },
          dataType: 'loaderData',
          exisitingUser,
        },
        { headers: { 'set-cookie': await sessionStorage.commitSession(session) } },
      )
    }
    return json({
      dataType: 'loaderData',
      currentDomain: process?.env?.CURRENT_ACTIVE_DOMAIN,
      currentSubDomain: process?.env?.COOKIE_SUBDOMAIN,
      exisitingUser,
    })
  } catch (err) {
    return {}
  }
}

export default function LoginPage() {
  const loaderData = useLoaderData<ActionData>()
  const actionData = useActionData<ActionData>()
  const { clearNavigationState } = useNavigationContext()
  const { ssoError = '' } = loaderData || {}
  const [existUserLocalStorage, removeExistUserLocalStorage] = useLocalStorage({
    key: 'existUserLocalStorage',
    defaultValue: { userExist: false },
  })
  const [tempPass, setTempPass] = useLocalStorage<any>({
    key: 'temp',
    defaultValue: {},
  })
  const isSubmitting = useNavigation().state === 'submitting' || useNavigation().state === 'loading'
  const [errorFlag, setErrorFlag] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState<string>('off')
  const [viewLess, setViewLess] = useState(true)
  const [isSsoFlowInitiated, SetIsSsoFlowInitiated] = useState(false)
  const [ssoLoginError, setSsoLoginError] = useState('')
  const submit = useSubmit()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (searchParams.get('sessionExpired') !== '1') {
      return
    }

    showCustomNotification({
      title: 'Info',
      message: SESSION_EXPIRED_MESSAGE,
      color: 'blue',
      autoClose: 8000,
    })

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('sessionExpired')
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    clearClientStateForLogin({
      currentDomain: loaderData?.currentDomain,
      currentSubDomain: loaderData?.currentSubDomain,
      existUserLocalStorageValue: safeLocalStorage?.getItem('existUserLocalStorage'),
    })
    if (tempPass) {
      safeLocalStorage.removeItem('temp')
    }
    if (existUserLocalStorage) {
      removeExistUserLocalStorage()
    }
    clearLoginSessionLocalStorage()
    clearNavigationState()
    setErrorFlag(true)
  }, [loaderData])
  useEffect(() => {
    if (actionData?.status === 'error') {
      setErrorFlag(true)
    }
  }, [actionData])

  const loginButtonClick = () => {
    safeLocalStorage.removeItem('passChanged')
    clientDeleteCookie(['usDt'])
    submit(
      {
        email: !containsOutsideLatin1(email) ? base64.encode(email) : '',
        password: !containsOutsideLatin1(password) ? base64.encode(password) : '',
        rememberMe: rememberMe,
      },
      { method: 'post' },
    )
  }

  const initiateSsoLogin = async (e: Event) => {
    SetIsSsoFlowInitiated(true)
    try {
      const redirectSsoURL = await fetch(`/api/initiateSsoLogin`)
      const formattedResponse = await redirectSsoURL?.json()
      if (formattedResponse?.detail) {
        setSsoLoginError(MAXIMUM_RETRY_SSO)
        SetIsSsoFlowInitiated(false)
        return
      }
      window.location.href = formattedResponse?.response?.authorization_url
    } catch (err) {
      SetIsSsoFlowInitiated(false)
      return null
    }
  }
  const passwordHandler = (val: string) => {
    setPassword(val)
    setTempPass({ passTemp: base64.encode(val) })
  }

  const handelEnterKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      loginButtonClick()
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const utms = manipulateUtm()
      setObjectCookie('sfmc', utms)
    }
    clearLogoutDomainAwareCookies(loaderData?.currentDomain, loaderData?.currentSubDomain)
    clearLoginSessionLocalStorage()
    purgeBrowserCookies()
  }, [])
  const isTab = useMediaQuery('(max-width: 950px)')

  const rememberMeStatus = (status: string) => {
    status ? setRememberMe('on') : setRememberMe('off')
  }
  const showExistingUserMessage = loaderData?.exisitingUser || existUserLocalStorage.userExist
  const emailErrors = [
    ...(loaderData?.errors?.fieldErrors?.email || []),
    ...(actionData?.errors?.fieldErrors?.email || []),
  ]
  return (
    <>
      <div
        className={showExistingUserMessage ? classes.eUsersvgContainerMobile : classes.svgContainer}
      >
        {!isTab && <img src={'/images/loginBg.png'} className={classes.backGroundImg} />}
      </div>
      <Container size="lg" className="topBarLogin"></Container>
      <Container size="lg" className={classes.logoContainer}>
        <Grid style={{ margin: 0 }}>
          <Grid.Col span={{ md: 5, sm: 12 }}>
            {' '}
            <Grid>
              <a href={REDIRECT_URL} className={classes.mpowerLogoLogin}>
                <img
                  src={'/images/LogoMPower.jpeg'}
                  className={classes.mpowerLogoLogin}
                  alt="MPOWER FINANCING"
                />
              </a>
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ md: 7, sm: 12 }}></Grid.Col>
        </Grid>
      </Container>
      <Form method="post" autoComplete="on">
        <Container size="lg" className={classes.mainContainer}>
          <Grid>
            <Grid.Col span={{ md: 5, sm: 12 }}>
              {!isTab && (
                <>
                  <Title order={3} variant="blue" fontWeight="500">
                    Think Globally,
                  </Title>
                  <Title order={3} variant="yellow" fontWeight="500" className="displayInline">
                    act locally!
                  </Title>
                </>
              )}
              {showExistingUserMessage && (
                <Flex className={classes.paper}>
                  <Flex className={classes.batchIconContainer}>
                    <Icon name="batchLogin" />
                  </Flex>
                  <Flex className={classes.iconTextWarpper}>
                    {/* <Text className={classes.eligibleText}>
                      Congratulations! You are eligible for an MPOWER student loan.{' '}
                      <span
                        className={classes.showMore}
                        onClick={() => setViewLess((value) => !value)}
                      >
                        {viewLess ? 'View less' : 'View more'}
                      </span>
                    </Text> */}

                    {viewLess && (
                      <Text className={clsx(classes.detailedDescription)}>
                        As an existing user, continue with your application by logging in to your
                        MPOWER Portal.
                      </Text>
                    )}
                  </Flex>
                </Flex>
              )}
            </Grid.Col>
            <Grid.Col span={{ sm: 12, md: 7 }}>
              <SsoLogin
                ssoLoginData={SSO_LOGIN_LIST}
                onClickAction={initiateSsoLogin}
                isSsoFlowInitiated={isSsoFlowInitiated}
                ssoError={ssoError}
                ssoErrorMsg={ssoLoginError}
              />
              <Text order={3} className={classes.welcomeHead}>
                {showExistingUserMessage ? 'Welcome Back' : 'Welcome'}
              </Text>
              {!showExistingUserMessage && (
                <Grid pb={10} px={0}>
                  <Grid.Col>
                    <Title
                      className={classes.signInText}
                      order={6}
                      fontWeight="400"
                      variant="black"
                    >
                      Sign into your MPOWER Portal Account for easy access to your application,
                      student profile, and additional services.
                    </Title>
                  </Grid.Col>
                </Grid>
              )}
              <Space h={10} />
              {errorFlag && emailErrors?.length > 0 && (
                <Title className={classes.validationText} order={6} fontWeight="700">
                  {emailErrors?.join(', ')}
                </Title>
              )}

              <TextField
                id={'email'}
                placeholder={'Enter your email ID'}
                label={'Your email ID'}
                maxLength="70"
                autoComplete="username"
                updatedValue={(email: string) => setEmail(email)}
                keyPress={handelEnterKeyPress}
              />
              <Space h={10} />
              <PasswordTextfield
                id={'password'}
                placeholder={'Enter your password'}
                label={'Password'}
                updatedValue={(pass: string) => passwordHandler(pass)}
                maxLength="20"
                autoComplete="current-password"
                keyPress={handelEnterKeyPress}
              />
              <Flex justify={'space-between'} align={'center'}>
                <Checkbox
                  label={<Text className={classes.confirmText}>Remember Me</Text>}
                  id={'rememberMe'}
                  size="sm"
                  updatedValue={(checkboxVal: string) => rememberMeStatus(checkboxVal)}
                />
                <Title className={classes.forgetPass} order={6} fontWeight="500">
                  <a
                    href="/login/emailChangePassword"
                    id="forgotPassword"
                    className={classes.forgotPassword}
                  >
                    Forgot Password?
                  </a>
                </Title>
              </Flex>

              <Space h={20} />
              <Grid className={classes.centerDiv}>
                <Grid.Col span={{ md: 12, sm: 12 }} className="alignCenter">
                  <Button
                    variant="filled"
                    uppercase={true}
                    loading={isSubmitting}
                    onClick={loginButtonClick}
                    id={'login'}
                    className={classes.loginBtn}
                  >
                    log in
                  </Button>
                  <Space h={20} />
                  <Divider
                    my="xs"
                    label={
                      <Title
                        className={classes.readyApply}
                        order={6}
                        fontWeight="700"
                        variant="blue"
                      >
                        Ready to apply for funding?
                      </Title>
                    }
                    labelPosition="center"
                  />

                  <Space h={20} />
                  <Flex className={classes.loginActionButton}>
                    <a href="/" id="startNewApp" className={classes.actionBtnStyle}>
                      <Button variant="tertiary" className={classes.actionBtnStyle}>
                        <div style={{ marginRight: '10px' }}>
                          <Icon name="startApplication" />
                        </div>
                        Start Loan application
                      </Button>
                    </a>
                    <a
                      href="https://www.mpowerfinancing.com/get-a-loan/refer"
                      id="referAfriend"
                      className={classes.actionBtnStyle}
                    >
                      <Button variant="tertiary" className={classes.actionBtnStyle}>
                        <div style={{ marginRight: '10px' }}>
                          <Icon name="refer" />
                        </div>
                        Refer a friend
                      </Button>
                    </a>
                  </Flex>
                  {isTab && (
                    <div className={classes.mobileHeadWrap}>
                      <div className="textInfoWrapper">
                        <Title
                          order={3}
                          variant="blue"
                          fontWeight="500"
                          className={classes.textStyle}
                        >
                          Think Globally,
                        </Title>
                        <Title
                          order={3}
                          variant="yellow"
                          fontWeight="500"
                          className={classes.locallyTextStyle}
                        >
                          act locally!
                        </Title>
                      </div>
                      <div className={classes.svgContainerMobile}>
                        {isTab && (
                          <img
                            className={classes.loginBgImageMobile}
                            src={'/images/loginBgMobile.png'}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Container>
      </Form>
    </>
  )
}
